import { useState, useMemo } from 'react';
import { Plus, Loader2, ShieldCheck, Pencil, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  useCancellationReasons,
  useCreateCancellationReason,
  useUpdateCancellationReason,
  useDeleteCancellationReason,
} from '@/hooks/api/useCancellationReasons';
import {
  CancelReasonCategory,
  type CancellationReasonCode,
} from '@pms/shared';

// ─── Constants ─────────────────────────────────────────────────────────────────

const CATEGORY_LABELS: Record<CancelReasonCategory, string> = {
  [CancelReasonCategory.GUEST]: 'Guest',
  [CancelReasonCategory.HOTEL]: 'Hotel',
  [CancelReasonCategory.NO_SHOW]: 'No-show',
  [CancelReasonCategory.OTA]: 'OTA',
};

const CATEGORY_OPTIONS = Object.values(CancelReasonCategory);

// ─── Page ────────────────────────────────────────────────────────────────────

export function CancellationReasonsPage() {
  const propertyId = useSelectedPropertyId();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<CancellationReasonCode | null>(null);
  const [search, setSearch] = useState('');

  const listQuery = useCancellationReasons({ propertyId }, !!propertyId);
  const deleteMutation = useDeleteCancellationReason();

  const allItems: CancellationReasonCode[] = listQuery.data?.data ?? [];

  const items: CancellationReasonCode[] = useMemo(() => {
    if (!search.trim()) return allItems;
    const q = search.toLowerCase();
    return allItems.filter(
      (r) =>
        r.label.toLowerCase().includes(q) || r.code.toLowerCase().includes(q),
    );
  }, [allItems, search]);

  function handleDelete(item: CancellationReasonCode) {
    if (!window.confirm('Delete this reason code?')) return;
    deleteMutation.mutate(item.id, {
      onError: (err) => {
        // Server soft-blocks when referenced by a booking (409 IN_USE).
        window.alert(extractErrorMessage(err));
      },
    });
  }

  return (
    <div>
      <PageHeader
        title="Cancellation Reasons"
        description="Reason codes for the cancel flow"
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setShowForm(true);
            }}
            disabled={!propertyId}
          >
            <Plus size={16} aria-hidden="true" />
            Add Reason Code
          </Button>
        }
      />

      <div className="mb-4">
        <input
          type="text"
          placeholder="Search by reason or code..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 w-64"
          aria-label="Search reason codes"
        />
      </div>

      {!propertyId ? (
        <EmptyState
          icon={ShieldCheck}
          title="Select a property"
          description="Select a property to continue."
        />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2
            size={32}
            className="animate-spin text-primary-500"
            aria-label="Loading"
          />
        </div>
      ) : listQuery.isError ? (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          icon={ShieldCheck}
          title="No reason codes"
          description="Create your first reason code."
          actionLabel="Add Reason Code"
          onAction={() => setShowForm(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Reason</Th>
                  <Th>Code</Th>
                  <Th>Category</Th>
                  <Th>Requires note</Th>
                  <Th>Active</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {r.label}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 font-mono">
                      {r.code}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700">
                      {CATEGORY_LABELS[r.category]}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {r.requiresNote ? 'Yes' : 'No'}
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
                          r.isActive
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-500',
                        )}
                      >
                        {r.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(r);
                            setShowForm(true);
                          }}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-primary-600 hover:bg-primary-50"
                          aria-label="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(r)}
                          className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50"
                          aria-label="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <SlideOver
        isOpen={showForm}
        onClose={() => {
          setShowForm(false);
          setEditing(null);
        }}
        title={editing ? 'Edit Reason Code' : 'Add Reason Code'}
        width="max-w-xl"
      >
        <CancellationReasonForm
          propertyId={propertyId}
          editing={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
        />
      </SlideOver>
    </div>
  );
}

// ─── Form ────────────────────────────────────────────────────────────────────

function CancellationReasonForm({
  propertyId,
  editing,
  onClose,
}: {
  propertyId: string;
  editing: CancellationReasonCode | null;
  onClose: () => void;
}) {
  const [label, setLabel] = useState(editing?.label ?? '');
  const [code, setCode] = useState(editing?.code ?? '');
  const [category, setCategory] = useState<CancelReasonCategory>(
    editing?.category ?? CancelReasonCategory.GUEST,
  );
  const [requiresNote, setRequiresNote] = useState(
    editing?.requiresNote ?? false,
  );
  const [isActive, setIsActive] = useState(editing?.isActive ?? true);

  const createMutation = useCreateCancellationReason();
  const updateMutation = useUpdateCancellationReason();
  const mutationError = createMutation.error ?? updateMutation.error;

  const isValid = label.trim().length > 0 && code.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
    if (updateMutation.isError) updateMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;

    if (editing) {
      updateMutation.mutate(
        {
          id: editing.id,
          payload: {
            label: label.trim(),
            code: code.trim(),
            category,
            requiresNote,
            isActive,
          },
        },
        { onSuccess: () => onClose() },
      );
    } else {
      createMutation.mutate(
        {
          propertyId,
          label: label.trim(),
          code: code.trim(),
          category,
          requiresNote,
          isActive,
        },
        { onSuccess: () => onClose() },
      );
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label
          htmlFor="reason-label"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Reason label <span className="text-red-500">*</span>
        </label>
        <input
          id="reason-label"
          type="text"
          required
          maxLength={255}
          value={label}
          onChange={(e) => {
            setLabel(e.target.value);
            clearError();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label
          htmlFor="reason-code"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Code <span className="text-red-500">*</span>
        </label>
        <input
          id="reason-code"
          type="text"
          required
          maxLength={100}
          value={code}
          onChange={(e) => {
            setCode(e.target.value);
            clearError();
          }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label
          htmlFor="reason-category"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Category <span className="text-red-500">*</span>
        </label>
        <select
          id="reason-category"
          value={category}
          onChange={(e) =>
            setCategory(e.target.value as CancelReasonCategory)
          }
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {CATEGORY_OPTIONS.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABELS[c]}
            </option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2">
        <input
          id="reason-requires-note"
          type="checkbox"
          checked={requiresNote}
          onChange={(e) => setRequiresNote(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label
          htmlFor="reason-requires-note"
          className="text-sm font-medium text-gray-700"
        >
          Requires note
        </label>
      </div>
      <div className="flex items-center gap-2">
        <input
          id="reason-active"
          type="checkbox"
          checked={isActive}
          onChange={(e) => setIsActive(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
        />
        <label
          htmlFor="reason-active"
          className="text-sm font-medium text-gray-700"
        >
          Active
        </label>
      </div>

      {mutationError && (
        <p
          className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2"
          role="alert"
        >
          {extractErrorMessage(mutationError)}
        </p>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="submit"
          isLoading={createMutation.isPending || updateMutation.isPending}
          disabled={!isValid}
        >
          {editing ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}

// ─── Shared ──────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50"
    >
      {children}
    </th>
  );
}
